import {Alert, Button, Checkbox, DateField, EmailField, Link, Textarea, TextField} from '@bonprix/design-system';
import {insertReactComponentIntoString} from '@service/scripts/components/helpers.jsx';
import {SubjectFormField} from '@service/scripts/components/contentful/contactform_v2/SubjectAndConcern.jsx';
import {useRef, useState} from 'react';

// Default client-side file upload settings (server remains authoritative)
const FILE_UPLOAD_DEFAULT_MAX_SIZE_MB = 1;
const FILE_UPLOAD_DEFAULT_MAX_COUNT = 3;
const FILE_UPLOAD_TOTAL_MAX_SIZE_MB = 10;
const FILE_UPLOAD_TOTAL_SIZE_RESERVE_BYTES = 512 * 1024;
const FILE_UPLOAD_TOTAL_MAX_SIZE_BYTES = FILE_UPLOAD_TOTAL_MAX_SIZE_MB * 1024 * 1024 - FILE_UPLOAD_TOTAL_SIZE_RESERVE_BYTES;
const FILE_UPLOAD_ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf'];
const FILE_UPLOAD_ACCEPT = FILE_UPLOAD_ALLOWED_MIME_TYPES.join(',');
const DEFAULT_FILE_UPLOAD_HINT = `Max. ${FILE_UPLOAD_DEFAULT_MAX_SIZE_MB} MB pro Datei · bis zu ${FILE_UPLOAD_DEFAULT_MAX_COUNT} Dateien · PNG, JPG, GIF, WebP, PDF`;
const FILE_UPLOAD_TOTAL_SIZE_ERROR = `Die Gesamtgröße aller Dateien darf ${FILE_UPLOAD_TOTAL_MAX_SIZE_MB} MB nicht überschreiten.`;


export function FormField({
                              formField,
                              formValues,
                              setFormValues,
                              formErrors,
                              tenantCountry,
                              handleChange,
                              fcContainer,
                              friendlyCaptchaSitekey,
                              friendlyCaptchaEnabled,
                              setIsSubmitEnabled,
                              uploadedFiles,
                              onFilesChange,
                              index
                          }) {
    if (formField && formField.type === 'CONTACT_FORM_SUBJECT_FIELD') {
        return <SubjectFormField formField={formField} formValues={formValues} formErrors={formErrors}
                                 handleChange={handleChange} setIsSubmitEnabled={setIsSubmitEnabled}
                                 setFormValues={setFormValues} />;
    }
    switch (formField.fieldType) {
        case 'TEXT':
            return <TextFormField formField={formField} formValues={formValues} formErrors={formErrors}
                                  handleChange={handleChange} index={index} />;

        case 'LONG_TEXT':
            return <TextAreaFormField formField={formField} formValues={formValues} formErrors={formErrors}
                                      handleChange={handleChange} index={index} />;
        case 'EMAIL':
            return <EmailFormField formField={formField} formValues={formValues} formErrors={formErrors}
                                   handleChange={handleChange} index={index} />;
        case 'NUMBER':
            return <NumberFormField formField={formField} formValues={formValues} formErrors={formErrors}
                                    handleChange={handleChange} index={index} />;
        case 'DATE':
            return <DateFormField formField={formField} formValues={formValues} formErrors={formErrors}
                                  handleChange={handleChange} index={index} />;
        case 'IBAN':
            return <IbanFormField formField={formField} formValues={formValues} formErrors={formErrors}
                                  handleChange={handleChange} index={index} />;
        case 'FRIENDLY_CAPTCHA':
            return <FriendlyCaptchaFormField formField={formField} formErrors={formErrors}
                                             fcContainer={fcContainer}
                                             friendlyCaptchaSitekey={friendlyCaptchaSitekey}
                                             friendlyCaptchaEnabled={friendlyCaptchaEnabled} />;


        case 'TERMS_AND_CONDITIONS':
            return <TermsAndConditionsFormField formField={formField} formValues={formValues}
                                                formErrors={formErrors} tenantCountry={tenantCountry}
                                                handleChange={handleChange} />;
        case 'FILE_UPLOAD':
            return <FileUploadFormField formField={formField} formErrors={formErrors}
                                        uploadedFiles={uploadedFiles} onFilesChange={onFilesChange} />;
    }
}


function getValue(formValues, formField, index) {
    if (typeof index === 'number') {
        return formValues[formField.name]?.[index] ?? '';
    }
    return formValues[formField.name] ?? '';
}

function NumberFormField({formField, formValues, formErrors, handleChange, index}) {
    return <div className="form-row">
        <TextField
            label={formField.label}
            maxLength={formField.maxLength}
            required={formField.mandatory}
            name={formField.name}
            inputMode="numeric"
            type="number"
            value={getValue(formValues, formField, index)}
            error={formErrors && !!formErrors[formField.name]}
            errorText={formErrors && formErrors[formField.name] && formField.errorText}
            onChange={event => handleChange(event, formField.name, index)}
        />
    </div>;
}


function DateFormField({formField, formValues, formErrors, handleChange, index}) {

    return <div className="form-row date-field">
        <DateField
            label={formField.label}
            maxLength={formField.maxLength}
            required={formField.mandatory}
            name={formField.name}
            inputMode="date"
            value={getValue(formValues, formField, index)}
            error={formErrors && !!formErrors[formField.name]}
            errorText={formErrors && formErrors[formField.name] && formField.errorText}
            onChange={event => handleChange(event, formField.name, index)}
        />
    </div>;
}


function IbanFormField({formField, formValues, formErrors, handleChange, index}) {
    return <div className="form-row">
        <TextField
            label={formField.label}
            maxLength={formField.maxLength || 34}
            required={formField.mandatory}
            name={formField.name}
            inputMode="text"
            value={getValue(formValues, formField, index)}
            error={formErrors && !!formErrors[formField.name]}
            errorText={formErrors && formErrors[formField.name] && formField.errorText}
            onChange={event => {
                // Convert to uppercase and remove spaces for IBAN format
                const upperValue = event.target.value.toUpperCase().replace(/\s/g, '');
                handleChange({target: {value: upperValue}}, formField.name, index);
            }}
        />
    </div>;
}


function EmailFormField({formField, formValues, formErrors, handleChange, index}) {
    return <div className="form-row">
        <EmailField
            label={formField.label}
            maxLength={formField.maxLength}
            required={formField.mandatory}
            inputMode="email"
            autoComplete="email"
            value={getValue(formValues, formField, index)}
            name={formField.name}
            error={formErrors && !!formErrors[formField.name]}
            errorText={formErrors && formErrors[formField.name] && formField.errorText}
            onChange={event => handleChange(event, formField.name, index)}
        />
    </div>;
}


function TextFormField({formField, formValues, formErrors, handleChange, index}) {
    return <div className="form-row">
        <TextField
            label={formField.label}
            minLength={formField.minLength}
            maxLength={formField.maxLength}
            required={formField.mandatory}
            name={formField.name}
            value={getValue(formValues, formField, index)}
            error={formErrors && !!formErrors[formField.name]}
            errorText={formErrors && formErrors[formField.name] && formField.errorText}
            onChange={event => handleChange(event, formField.name, index)}
        />
    </div>;
}

function TextAreaFormField({formField, formValues, formErrors, handleChange, index}) {
    return <div className="form-row">
        <Textarea
            label={formField.label}
            minLength={formField.minLength}
            maxLength={formField.maxLength}
            required={formField.mandatory}
            name={formField.name}
            value={getValue(formValues, formField, index)}
            onChange={event => handleChange(event, formField.name, index)}
            error={formErrors && !!formErrors[formField.name]}
            errorText={formErrors && formErrors[formField.name] && formField.errorText}
        />
    </div>;
}

function FriendlyCaptchaFormField({
                                      formField,
                                      formErrors,
                                      fcContainer,
                                      friendlyCaptchaSitekey,
                                      friendlyCaptchaEnabled
                                  }) {
    return friendlyCaptchaEnabled ? <div className="form-row">
        <div
            ref={fcContainer}
            className="frc-captcha"
            data-sitekey={friendlyCaptchaSitekey}
            data-lang="de"
        ></div>
        {formErrors && formErrors['frc-captcha-solution'] && (
            <Alert isOpen status="error" showCloseIcon={false}>
                {formField.errorText}
            </Alert>
        )}
    </div> : null;
}

function TermsAndConditionsFormField({formField, formValues, formErrors, tenantCountry, handleChange}) {
    const dataProtectionUrl = tenantCountry === 'at'
        ? '/serviceleistung/datenschutz/'
        : '/service/datenschutz/';

    const dataProtectionLabel = (
        <span>
            {insertReactComponentIntoString(
                formField.label,
                '#dataProtectionLink#',
                <Link href={dataProtectionUrl}>Datenschutzerklärung</Link>
            )}
        </span>
    );

    return <div className="form-row">
        <Checkbox
            label={dataProtectionLabel}
            required
            name={formField.name}
            checked={formValues[formField.name]}
            onChange={event => handleChange(event, formField.name)}
            error={formErrors && !!formErrors[formField.name]}
            errorText={
                formErrors &&
                formErrors[formField.name] &&
                formField.errorText
            }
        />
    </div>;
}

function FileUploadFormField({formField, formErrors, uploadedFiles, onFilesChange}) {
    const inputRef = useRef();
    const [clientError, setClientError] = useState('');

    // Derive per-field config (Contentful fields: fileUploadMaxSizeMb, fileUploadMaxCount, fileUploadHint)
    const maxSizeMb = formField && formField.fileUploadMaxSizeMb ? Number(formField.fileUploadMaxSizeMb) : FILE_UPLOAD_DEFAULT_MAX_SIZE_MB;
    const maxCount = formField && formField.fileUploadMaxCount ? Number(formField.fileUploadMaxCount) : FILE_UPLOAD_DEFAULT_MAX_COUNT;
    const maxBytes = maxSizeMb * 1024 * 1024;
    const hintText = formField && formField.fileUploadHint ? formField.fileUploadHint : DEFAULT_FILE_UPLOAD_HINT;

    const handleFileSelect = (event) => {
        const selected = Array.from(event.target.files);
        // Reset so the same file can be re-selected after removal
        event.target.value = '';

        const nextFiles = [...uploadedFiles];
        let validationError = '';

        for (const file of selected) {
            if (nextFiles.length >= maxCount) {
                validationError = `Sie können höchstens ${maxCount} Dateien anhängen.`;
                break;
            }
            if (file.size > maxBytes) {
                validationError = formField.errorText;
                break;
            }
            if (!FILE_UPLOAD_ALLOWED_MIME_TYPES.includes(file.type)) {
                validationError = formField.errorText;
                break;
            }
            if (nextFiles.reduce((total, currentFile) => total + currentFile.size, 0) + file.size > FILE_UPLOAD_TOTAL_MAX_SIZE_BYTES) {
                validationError = FILE_UPLOAD_TOTAL_SIZE_ERROR;
                break;
            }
            nextFiles.push(file);
        }

        setClientError(validationError);
        if (nextFiles.length !== uploadedFiles.length) {
            onFilesChange(nextFiles);
        }
    };

    const removeFile = (index) => {
        setClientError('');
        onFilesChange(uploadedFiles.filter((_, i) => i !== index));
    };

    const serverError = formErrors?.[formField.name];
    const errorText = clientError
        || (serverError === 'errorFilesTotalSize' ? FILE_UPLOAD_TOTAL_SIZE_ERROR : '')
        || (serverError === 'errorFilesCount' ? `Sie können höchstens ${maxCount} Dateien anhängen.` : '')
        || (serverError ? formField.errorText : '');
    const hasError = !!errorText;
    const canAddMore = uploadedFiles.length < maxCount;

    return <div className="form-row">
        <input
            ref={inputRef}
            type="file"
            accept={FILE_UPLOAD_ACCEPT}
            className="sr-only"
            name={formField.name}
            onChange={handleFileSelect}
        />
        <fieldset className="preflight border-0 p-0 m-0">
            <legend className={`typography-label mb-2${formField.mandatory ? ' after:content-[\'_*\']' : ''}`}>
                {formField.label}
            </legend>
            <p className="typography-body-small text-clr-fg-secondary mb-2">{hintText}</p>
            {uploadedFiles.map((file, i) => (
                <div key={`${file.name}-${file.size}-${file.lastModified}`} className="preflight flex items-center gap-2 mb-1">
                    <span className="typography-body-small truncate">{file.name}</span>
                    <button
                        type="button"
                        className="preflight ml-auto shrink-0 text-clr-fg-secondary hover:text-clr-fg-primary"
                        aria-label={`${file.name} entfernen`}
                        onClick={() => removeFile(i)}
                    >
                        ✕
                    </button>
                </div>
            ))}
            {canAddMore && (
                <Button variant="secondary" onClick={() => inputRef.current.click()}>
                    {uploadedFiles.length === 0 ? 'Datei anhängen' : 'Weitere Datei anhängen'}
                </Button>
            )}
        </fieldset>
        {hasError && (
            <Alert isOpen status="error" showCloseIcon={false}>
                {errorText}
            </Alert>
        )}
    </div>;
}
